import { useState } from 'react';
import { Box } from '@mui/material';
import { PieceHighlights } from './PieceHighlights';
import { PieceGantt } from './PieceGantt';
import { PieceStats } from './PieceStats';
import { PieceSkills } from './PieceSkills';
import { PieceWorkload } from './PieceWorkload';
import { HERO_DOT_ACTIVE, HERO_DOT_INACTIVE } from '../designTokens';

const PIECES = [
  { id: 'highlights', Component: PieceHighlights },
  { id: 'gantt',      Component: PieceGantt      },
  { id: 'stats',      Component: PieceStats      },
  { id: 'skills',     Component: PieceSkills     },
  { id: 'workload',   Component: PieceWorkload   },
] as const;

export type PieceId = (typeof PIECES)[number]['id'];

interface RotatingHeroProps {
  pieceIdx: number;
}

/** Renders the active hero piece with a crossfade entrance. */
export function RotatingHero({ pieceIdx }: RotatingHeroProps) {
  const { Component } = PIECES[pieceIdx];
  return (
    <Box
      key={pieceIdx}
      sx={{
        '@keyframes ppHeroFadeIn': {
          from: { opacity: 0, transform: 'translateY(4px)' },
          to:   { opacity: 1, transform: 'none' },
        },
        animation: 'ppHeroFadeIn 0.35s ease',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <Component />
    </Box>
  );
}

interface HeroDotsProps {
  pieceIdx: number;
  onSelect: (i: number) => void;
}

/** Pill-dot navigation — active dot expands horizontally. */
export function HeroDots({ pieceIdx, onSelect }: HeroDotsProps) {
  return (
    <Box
      role="group"
      aria-label="Hero content navigation"
      sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}
    >
      {PIECES.map((p, i) => (
        <Box
          key={p.id}
          component="button"
          aria-label={`Show ${p.id}`}
          aria-pressed={i === pieceIdx}
          onClick={() => onSelect(i)}
          sx={{
            width: i === pieceIdx ? 18 : 6,
            height: 6,
            borderRadius: 999,
            background: i === pieceIdx ? HERO_DOT_ACTIVE : HERO_DOT_INACTIVE,
            border: 0,
            p: 0,
            cursor: 'pointer',
            transition: 'width 0.2s, background 0.2s',
            '&:focus-visible': {
              outline: `2px solid ${HERO_DOT_ACTIVE}`,
              outlineOffset: 2,
            },
          }}
        />
      ))}
    </Box>
  );
}

/** Re-randomises the piece on every mount — Phase 0 Q5. */
export function useRotatingHero() {
  const [pieceIdx, setPieceIdx] = useState<number>(
    () => Math.floor(Math.random() * PIECES.length),
  );
  return { pieceIdx, setPieceIdx };
}
